/** The top bar: the battle's title, each side's units and rounds (coloured
 *  by trend, rates on hover), a commander's resources (BAR's bar: stock and
 *  change per minute), the clock and Menu. Its state is a store the game
 *  writes each frame (`ui/hud.ts`). */

import { For, Show, type JSX } from "solid-js";
import { createStore } from "solid-js/store";
import { render } from "solid-js/web";
import { setTip } from "../ui/tooltip";
import { Spark } from "../screens/Production";
import { GoodIcon } from "../console/RecipePicker";
import { command, type Command } from "../state/features";

/** Command points in words (BAR's command limit, here per formation). */
const commandTip = (c: Command) =>
  `Command: ${c.used} of ${c.capacity} points in use. A formation takes a point per ${c.perPoint} units (at least one). Your home gives 40, each comms post 20 more.` +
  (c.uncommanded.size ? ` ${c.uncommanded.size === 1 ? "One formation is" : `${c.uncommanded.size} formations are`} over capacity: they hold and take no orders until there's room.` : "");
import { TICKS_PER_MINUTE, samples } from "../state/history";

/** The icon a resource on the bar shows, by its label. */
const BAR_ICON: Record<string, string> = { Steel: "Steel", Rounds: "Munitions", Recruits: "Recruits", People: "People", Credits: "Credits" };
import { toggleScreen } from "../screens/Screens";

export interface Side {
  name: string;
  color: string;
  /** Its commander's avatar, if signed in. */
  avatar?: string;
  units: string;
  /** "321k rounds" (blank when unknown, e.g. the enemy's in fog). */
  rounds: string;
  unitsTrend: Trend;
  roundsTrend: Trend;
}

export type Trend = "up" | "down" | "";

/** A good in the Goods panel: every good the commander has, in full. */
export interface GoodRow {
  name: string;
  /** Its colour (ores: the atlas's), or blank. */
  color: string;
  stock: string;
  made: string;
  used: string;
  net: string;
  trend: Trend;
  /** Room for it ("/ 9,000"), where known. */
  room: string;
}

/** One of a commander's resources: its stock and change per minute. */
export interface Resource {
  label: string;
  value: string;
  /** "+40/min" (blank until measured, or when steady). */
  rate: string;
  trend: Trend;
  /** Made and used per minute ("+48", "−30"), when the server reports
   *  them (BAR's income and expense); blank otherwise, and `rate` shows. */
  income: string;
  expense: string;
  /** The army's upkeep a minute ("−120"), steel only; and whether it went
   *  unpaid (red). */
  upkeep?: string;
  unpaid?: boolean;
}

export const [bar, setBar] = createStore({
  title: "",
  clock: "",
  sides: [] as Side[],
  /** A commander's own side (their figures only, as BAR shows your own
   *  resources); null shows every side. */
  own: null as number | null,
  /** The tooltip per side (worked out on hover). */
  tips: [] as (() => string)[],
  /** A commander's resources (none for spectators and replays), and the
   *  tooltip of each (where it is, what makes and spends it). */
  resources: [] as Resource[],
  resourceTip: ((_i: number) => "") as (i: number) => string,
  /** A commander's idle engineers (BAR's idle-builder button): how many,
   *  the key, and selecting the next. Hidden at zero. */
  idle: { count: 0, key: "", tip: "", run: () => {} } as { count: number; key: string; tip: string; run: () => void },
  /** Every good (the Goods panel), and whether it's open. */
  goods: [] as GoodRow[],
  goodsOpen: false,
  menu: (() => {}) as () => void,
  /** Depth screens a commander can open (Research...), with their keys. */
  screens: [] as {
    label: string;
    key: string;
    open: boolean;
    run: () => void;
    /** Something waiting there (offers of alliance), if any. */
    badge?: number;
  }[],
});

function TopBar(): JSX.Element {
  return (
    <>
      <div class="title">
        <span id="title">{bar.title}</span>
      </div>
      <div class="forces">
        <For each={bar.sides}>
          {(side, i) => (
            <Show when={bar.own === null || bar.own === i()}>
              <div
                class="force"
                ref={(el) => setTip(el, () => bar.tips[i()]?.() ?? "")}
              >
                {/* Your own side: its colour, not a numbered name. */}
                <Show
                  when={bar.own === null}
                  fallback={
                    <Show when={side.avatar} fallback={<span class="own-swatch" style={{ background: side.color }} />}>
                      {(src) => <img class="avatar" src={src()} alt="" width="20" height="20" style={{ "border-color": side.color }} />}
                    </Show>
                  }
                >
                  <Show when={side.avatar}>{(src) => <img class="avatar" src={src()} alt="" width="18" height="18" style={{ "border-color": side.color }} />}</Show>
                  <span class="name" style={{ color: side.color }}>
                    {side.name}
                  </span>
                </Show>
                <span
                  class="big"
                  classList={{
                    up: side.unitsTrend === "up",
                    down: side.unitsTrend === "down",
                  }}
                >
                  {side.units}
                </span>
                {/* A commander's rounds are a resource of their own. */}
                <Show
                  when={
                    side.rounds && bar.own === null && bar.sides.length <= 4
                  }
                >
                  <span
                    class="detail"
                    classList={{
                      up: side.roundsTrend === "up",
                      down: side.roundsTrend === "down",
                    }}
                  >
                    {side.rounds}
                  </span>
                </Show>
              </div>
            </Show>
          )}
        </For>
      </div>
      <Show when={bar.idle.count > 0}>
        <button class="ghost idle-engineers" data-tip={bar.idle.tip || "Engineers standing idle: select the next"} onClick={() => bar.idle.run()}>
          Idle engineers <b>{bar.idle.count}</b>
          <kbd>{bar.idle.key}</kbd>
        </button>
      </Show>
      <Show when={bar.resources.length}>
        <div class="resources">
          <For each={bar.resources}>
            {(r, i) => (
              <div class="resource" ref={(el) => setTip(el, () => bar.resourceTip(i()))}>
                <Show when={BAR_ICON[r.label]}>{(g) => <GoodIcon good={g()} size={18} />}</Show>
                <span class="label">{r.label}</span>
                <span class="big">{r.value}</span>
                <Show
                  when={r.income || r.expense}
                  fallback={
                    <span
                      class="rate"
                      classList={{ up: r.trend === "up", down: r.trend === "down" }}
                    >
                      {r.rate}
                    </span>
                  }
                >
                  <span class="rate up">{r.income}</span>
                  <span class="rate down">{r.expense}</span>
                </Show>
                <Show when={r.upkeep}>
                  <span class="rate upkeep" classList={{ unpaid: !!r.unpaid }}>
                    {r.upkeep} upkeep
                  </span>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>
      <Show when={command()}>
        {(c) => (
          <div class="resource command" classList={{ over: c().used > c().capacity }} data-tip={commandTip(c())}>
            <span class="label">Command</span>
            <span class="big">
              {c().used}/{c().capacity}
            </span>
          </div>
        )}
      </Show>
      <Show when={bar.goods.length}>
        <div class="goods-menu">
          <button class="ghost" aria-expanded={bar.goodsOpen} onClick={() => setBar("goodsOpen", !bar.goodsOpen)}>
            Goods ▾
          </button>
          <Show when={bar.goodsOpen}>
            <div class="goods-panel" role="table" aria-label="Every good">
              <div class="goods-row head" role="row">
                <span>Good</span>
                <span>Stock</span>
                <span>Made</span>
                <span>Used</span>
                <span>Net/min</span>
                <span>
                  {(() => {
                    const sm = samples();
                    const m = sm.length > 1 ? Math.round((sm[sm.length - 1].tick - sm[0].tick) / TICKS_PER_MINUTE) : 0;
                    return m >= 60 ? "Last hour" : `Last ${Math.max(1, m)} min`;
                  })()}
                </span>
              </div>
              <For each={bar.goods}>
                {(g) => (
                  <div class="goods-row" role="row">
                    <span class="good-name">
                      <GoodIcon good={g.name} size={16} />
                      {g.name}
                    </span>
                    <span>
                      {g.stock}
                      <small>{g.room}</small>
                    </span>
                    <span class="up">{g.made}</span>
                    <span class="down">{g.used}</span>
                    <span classList={{ up: g.trend === "up", down: g.trend === "down" }}>{g.net}</span>
                    <Spark good={g.name} />
                  </div>
                )}
              </For>
              <button
                class="ghost goods-graphs"
                onClick={() => {
                  setBar("goodsOpen", false);
                  toggleScreen("production");
                }}
              >
                Production
              </button>
            </div>
          </Show>
        </div>
      </Show>
      <Show when={bar.clock}>
        <div class="top-clock">{bar.clock}</div>
      </Show>
      <For each={bar.screens}>
        {(s) => (
          <button class="ghost" aria-pressed={s.open} onClick={() => s.run()}>
            {s.label}
            <Show when={s.badge}>
              <span class="badge">{s.badge}</span>
            </Show>
            <Show when={s.key}>
              <kbd>{s.key}</kbd>
            </Show>
          </button>
        )}
      </For>
      <button class="ghost" onClick={() => bar.menu()}>
        Menu
      </button>
    </>
  );
}

render(() => <TopBar />, document.getElementById("hud")!);

// The Goods panel closes on a click anywhere else, or Esc (before Esc
// reaches the game).
document.addEventListener("pointerdown", (e) => {
  if (bar.goodsOpen && !(e.target as HTMLElement).closest(".goods-menu")) setBar("goodsOpen", false);
});
window.addEventListener(
  "keydown",
  (e) => {
    if (e.key !== "Escape" || !bar.goodsOpen) return;
    setBar("goodsOpen", false);
    e.stopImmediatePropagation();
    e.preventDefault();
  },
  { capture: true },
);
